export const FOV_CONTROL_WIDTH = 200
export const FOV_CONTROL_HEIGHT = 104
/** Same inset as the 4px of canvas above the arc on the original 104px board. */
export const FOV_CROP_MARGIN = 4
export const FOV_PANEL_PAD_PX = 8
export const FOV_PANEL_BORDER_PX = 1

export const FOV_APEX_X = 100
export const FOV_APEX_Y = 100
export const FOV_RADIUS = 96
export const FOV_APEX_DOT = 2.5
export const FOV_VALUE_TEXT_Y = 66
export const FOV_LABEL_INSET = 15
export const FOV_MAJOR_TICK = { length: 6, width: 1.2 }
export const FOV_MINOR_TICK = { length: 3, width: 0.8 }
export const FOV_FAN_STROKE = 1
export const FOV_VALUE_STROKE = 1.2
export const FOV_FOCUS_STROKE = 2
export const FOV_SELECT_TOLERANCE = 0.6

export const FOV_LABELED_FOCALS = [10, 14, 24, 50] as const
export const FOV_MINOR_FOCALS = [12, 16, 18, 20, 28, 35, 40] as const
export const FOV_FRAME_HEIGHT_MM = 24

/** Vertical FOV in degrees for a full-frame lens. `frameHeight / 2` is 12 mm at 24 mm. */
export function fovOfFocal(mm: number, frameHeight = FOV_FRAME_HEIGHT_MM): number {
  return (2 * Math.atan(frameHeight / 2 / mm) * 180) / Math.PI
}

export function clampFov(value: number, min: number, max: number, fallback: number): number {
  const n = Math.round(Number(value))
  if (!Number.isFinite(n)) return fallback
  return Math.min(max, Math.max(min, n))
}

/** Tick stays when the rounded degree sits inside the camera range (10 mm → 100°). */
export function focalInRange(mm: number, min: number, max: number, frameHeight = FOV_FRAME_HEIGHT_MM): boolean {
  const degrees = Math.round(fovOfFocal(mm, frameHeight))
  return degrees >= min && degrees <= max
}

/** Angle in degrees from vertical, positive to the right. Screen Y grows downward. */
export function pointOnFan(radius: number, angleDeg: number): [number, number] {
  const a = (angleDeg * Math.PI) / 180
  return [FOV_APEX_X + radius * Math.sin(a), FOV_APEX_Y - radius * Math.cos(a)]
}

export function sectorPath(radius: number, halfDeg: number): string {
  const [x1, y1] = pointOnFan(radius, -halfDeg)
  const [x2, y2] = pointOnFan(radius, halfDeg)
  const f = (n: number) => +n.toFixed(3)
  return `M${FOV_APEX_X} ${FOV_APEX_Y}L${f(x1)} ${f(y1)}A${radius} ${radius} 0 0 1 ${f(x2)} ${f(y2)}Z`
}

export type FovControlFrame = {
  x: number
  y: number
  width: number
  height: number
}

/** Visible board: the max-angle sector plus {@link FOV_CROP_MARGIN} on every side. */
export function fovControlFrame(maxDeg: number): FovControlFrame {
  const half = ((maxDeg / 2) * Math.PI) / 180
  const span = FOV_RADIUS * Math.sin(half)
  const x = FOV_APEX_X - span - FOV_CROP_MARGIN
  const y = FOV_APEX_Y - FOV_RADIUS - FOV_CROP_MARGIN
  const width = span * 2 + FOV_CROP_MARGIN * 2
  const height = FOV_RADIUS + FOV_APEX_DOT + FOV_CROP_MARGIN * 2
  return { x, y, width, height }
}

/** Popover box for a fan panel with `p-2` and a 1px border. */
export function fovMenuSize(maxDeg: number): { width: number; height: number } {
  const frame = fovControlFrame(maxDeg)
  const chrome = FOV_PANEL_PAD_PX * 2 + FOV_PANEL_BORDER_PX * 2
  return {
    width: frame.width + chrome,
    height: frame.height + chrome,
  }
}

/**
 * Unclamped degrees from a vertical drag. `deltaY` is positive downward.
 * One board-height of travel covers `max − min`.
 */
export function fovFromVerticalDrag(
  startValue: number,
  deltaY: number,
  height: number,
  min: number,
  max: number,
): number {
  if (!(height > 0)) return startValue
  return startValue - (deltaY * (max - min)) / height
}
